import React from 'react';
import { Await, Link } from 'react-router';

import { makeApiRequestHeaders } from '~/api/headers';
import { isLoggedIn } from '~/api/is-logged-in';
import { Type, surfaceNewsV1 } from '~/api/v1/mall/surface-news';
import { surfaceV1 } from '~/api/v1/mall/surface/surface-name';
import { campaignSearchV2 } from '~/api/v2/mall/campaign/search';
import { commonDataV2Cached } from '~/api/v2/mall/common-data';
import { recommendShopV2 } from '~/api/v2/mall/recommend-shop';
import { segmentV2 } from '~/api/v2/mall/segment';
import { CampaignTypeV2 } from '~/api/v2/schemas/campaign-type';
import { RewardTypeV2 } from '~/api/v2/schemas/reward-type';
import { Category } from '~/features/category';
import { TopFullBanner, makeTopFullBannerLoader } from '~/features/top-full-banner';
import { AwaitErrorElement } from '~/ui/await-error-element';
import { isDeviceMobileOrTablet } from '~/utils';

import { SiteGlobalMenu } from '../components/site-global-menu';
import { SiteMainContents } from '../components/site-main-contents';
import { SitePointupCarousel } from '../components/site-pointup-carousel';
import { SiteRecommendShopLogo } from '../components/site-recommend-shop-logo';
import { SiteShopRanking } from '../components/site-shop-ranking';
import { categoryIcons } from '../config';
import { categoryStyles, topFullBannerStyles } from '../styles';
import { metaTitle } from '../utils/meta-title';
import type { Route } from './+types/_index';

export const meta: Route.MetaFunction = () => {
  return [{ title: metaTitle('お買い物TOP') }];
};

const topFullBannerLoader = makeTopFullBannerLoader({
  surfaceApi: surfaceV1,
});

export const loader = async ({ request }: Route.LoaderArgs) => {
  const url = new URL(request.url);
  const path = url.pathname;
  const isMobileOrTablet = isDeviceMobileOrTablet(request.headers.get('user-agent'));

  const headers = await makeApiRequestHeaders(request);
  const loggedIn = await isLoggedIn(request);

  // Promiseとして返すAPI（<Await>で処理）
  const promises = {
    recommendShopPromise: recommendShopV2(headers, { limit: 8 }),
    importantNewsPromise: surfaceNewsV1(headers, { type: Type.IMPORTANT }),
    shopRankingPromise: campaignSearchV2(headers, {
      campaign_type: CampaignTypeV2.SHOP,
      reward_type: RewardTypeV2.RATE,
      limit: 10,
    }),
    pointupShopPromise: campaignSearchV2(headers, {
      campaign_type: CampaignTypeV2.SHOP,
      reward_type: RewardTypeV2.RATE,
      pointup_flg: true,
    }),
    commonDataPromise: commonDataV2Cached(headers),
    segmentPromise: loggedIn ? segmentV2(headers) : undefined,
    topSlideBannerPromise: surfaceV1(headers, 'top_slide_banner'),
    topFullBannerPromise: topFullBannerLoader(request),
  };

  return {
    ...promises,
    path,
    isMobileOrTablet,
  };
};

export default function Index({
  loaderData: {
    topSlideBannerPromise,
    recommendShopPromise,
    importantNewsPromise,
    shopRankingPromise,
    commonDataPromise,
    segmentPromise,
    pointupShopPromise,
    topFullBannerPromise,
    path,
    isMobileOrTablet,
  },
}: Route.ComponentProps) {
  return (
    <div className="mb-18">
      <SiteMainContents
        importantNewsPromise={importantNewsPromise}
        recommendShopPromise={recommendShopPromise}
        topSlideBannerPromise={topSlideBannerPromise}
        path={path}
      />

      <div className="lg:main-container mt-4 mb-16 md:mt-14">
        <SiteGlobalMenu isMobileOrTablet={isMobileOrTablet} />
      </div>

      <div className="flex flex-col gap-16 md:gap-30">
        <div>
          {/* Display recommended shops section only when:
         - Promise resolves successfully
         - Response contains shops array (recommendShop.shops)
         - At least one shop exists (recommendShop.shops.length > 0)
         おすすめショップセクションの表示条件:
         - Promiseが正常に解決された場合
         - レスポンスにshops配列が含まれる場合（recommendShop.shops）
         - 少なくとも1つのショップが存在する場合（recommendShop.shops.length > 0）*/}
          {recommendShopPromise && (
            <React.Suspense fallback={null}>
              <Await
                resolve={recommendShopPromise}
                errorElement={<AwaitErrorElement message="recommendShop Promise failed" />}>
                {(recommendShop) =>
                  recommendShop.shops &&
                  recommendShop.shops.length > 0 && (
                    <>
                      <h2 className="text-headline-xs main-container">あなたにおすすめ</h2>
                      <div className="mt-6 mb-16 md:mb-6">
                        <SiteRecommendShopLogo recommendShopDetail={recommendShop.shops} />
                      </div>
                    </>
                  )
                }
              </Await>
            </React.Suspense>
          )}

          {/* Display top full banner only when:
         - Promise resolves successfully
         - Response contains banner data (topFullBanner.banner)
         トップフルバナーの表示条件:
         - Promiseが正常に解決された場合
         - レスポンスにバナーデータが含まれる場合（topFullBanner.banner）*/}
          {topFullBannerPromise && (
            <React.Suspense fallback={null}>
              <Await
                resolve={topFullBannerPromise}
                errorElement={<AwaitErrorElement message="topFullBanner Promise failed" />}>
                {(topFullBanner) =>
                  topFullBanner.banner && (
                    <div className="main-container md:max-w-[980px]">
                      <TopFullBanner styles={topFullBannerStyles} topFullBanner={topFullBanner.banner}></TopFullBanner>
                    </div>
                  )
                }
              </Await>
            </React.Suspense>
          )}
        </div>

        {/* Display pointup shops carousel only when:
         - Both promises resolve successfully
         - Response contains campaign info (pointupShop.campaign_info)
         - At least one campaign exists (pointupShop.campaign_info.length > 0)
         ポイントアップショップカルーセルの表示条件:
         - 両方のPromiseが正常に解決された場合
         - レスポンスにキャンペーン情報が含まれる場合（pointupShop.campaign_info）
         - 少なくとも1つのキャンペーンが存在する場合（pointupShop.campaign_info.length > 0）*/}
        {commonDataPromise && pointupShopPromise && (
          <React.Suspense fallback={null}>
            <Await resolve={commonDataPromise} errorElement={<AwaitErrorElement message="commonData Promise failed" />}>
              {(commonData) => (
                <Await
                  resolve={pointupShopPromise}
                  errorElement={<AwaitErrorElement message="pointupShop Promise failed" />}>
                  {(pointupShop) =>
                    pointupShop.campaign_info &&
                    pointupShop.campaign_info.length > 0 && (
                      <div>
                        <div className="main-container mb-3 px-4">
                          <h2 className="text-headline-xs">還元率UP中！</h2>
                        </div>
                        <SitePointupCarousel
                          commonData={commonData}
                          segment={segmentPromise}
                          campaignSearch={pointupShop}
                        />
                      </div>
                    )
                  }
                </Await>
              )}
            </Await>
          </React.Suspense>
        )}

        {/* Display shop ranking section only when:
         - Both promises resolve successfully
         - Campaign info exists (shopRanking.campaign_info)
         - At least one campaign exists (shopRanking.campaign_info.length > 0)
         ショップランキングセクションの表示条件:
         - 両方のPromiseが正常に解決された場合
         - キャンペーン情報が存在する場合（shopRanking.campaign_info）
         - 少なくとも1つのキャンペーンが存在する場合（shopRanking.campaign_info.length > 0）*/}
        {commonDataPromise && shopRankingPromise && (
          <React.Suspense fallback={null}>
            <Await resolve={commonDataPromise} errorElement={<AwaitErrorElement message="commonData Promise failed" />}>
              {(commonData) => (
                <Await
                  resolve={shopRankingPromise}
                  errorElement={<AwaitErrorElement message="shopRanking Promise failed" />}>
                  {(shopRanking) =>
                    shopRanking.campaign_info &&
                    shopRanking.campaign_info.length > 0 && (
                      <div className="main-container">
                        <div className="mb-6 flex items-center justify-between">
                          <h2 className="text-headline-xs">人気のお店</h2>
                          <Link
                            to={`${import.meta.env.VITE_CAMPAIGN_SEARCH_ROUTE}?campaign_type=1&reward_type=1`}
                            className="text-body-s text-blue-500 hover:underline"
                            aria-label="人気のお店すべて見る">
                            すべて見る
                          </Link>
                        </div>

                        <SiteShopRanking
                          commonData={commonData}
                          campaignSearch={shopRanking}
                          segment={segmentPromise}
                        />
                      </div>
                    )
                  }
                </Await>
              )}
            </Await>
          </React.Suspense>
        )}

        {/* Display category section only when:
         - Promise resolves successfully
         - Response contains categories (commonData.categories)
         カテゴリーセクションの表示条件:
         - Promiseが正常に解決された場合
         - レスポンスにカテゴリーが含まれる場合（commonData.categories）*/}
        {commonDataPromise && (
          <React.Suspense fallback={null}>
            <Await resolve={commonDataPromise} errorElement={<AwaitErrorElement message="commonData Promise failed" />}>
              {(commonData) =>
                commonData.categories && (
                  <div className="main-container">
                    <h2 className="text-headline-xs mb-6">カテゴリーから探す</h2>
                    <Category
                      styles={categoryStyles}
                      categories={commonData.categories}
                      categoryIcons={categoryIcons}
                      rewardType={RewardTypeV2.RATE}
                    />
                  </div>
                )
              }
            </Await>
          </React.Suspense>
        )}
      </div>
    </div>
  );
}
